import { api } from '../lib/api-client.js';

import type {
  AiClassificationAcceptInput,
  AiQuotaPauseInput,
  AiQuotaUpdateInput,
  ReviewQueueAcceptInput,
  ReviewQueueRerouteInput,
  ReviewQueueRejectInput,
  VoiceStructureRequest,
  VoiceStructureToolResult,
  VoiceTranscribeRequest,
} from '@sitewise/shared';


/**
 * Phase 8 — AI client surface. Mirrors the routes registered under
 * `/api/v1/ai/*`. Each function returns the parsed JSON; errors throw
 * via `api()` so React Query observes them.
 */

// ---------- Review queue ----------

export interface ReviewQueueItem {
  id: string;
  fromEmail: string;
  subject: string;
  receivedAt: string;
  suggestedWorkOrderId: string | null;
  suggestedWorkOrderNumber: string | null;
  suggestedWorkOrderTitle: string | null;
  confidence: number | null;
  suggestedAction: string | null;
  summary: string | null;
}

export async function fetchReviewQueue(): Promise<{ items: ReviewQueueItem[] }> {
  return api('/ai/review-queue');
}

export async function acceptReviewItem(
  id: string,
  input: ReviewQueueAcceptInput,
): Promise<{ status: string; workOrderId: string }> {
  return api(`/ai/review-queue/${id}/accept`, { method: 'POST', body: input });
}

export async function rerouteReviewItem(
  id: string,
  input: ReviewQueueRerouteInput,
): Promise<{ status: string; workOrderId: string }> {
  return api(`/ai/review-queue/${id}/reroute`, { method: 'POST', body: input });
}

export async function rejectReviewItem(
  id: string,
  input: ReviewQueueRejectInput,
): Promise<{ status: string }> {
  return api(`/ai/review-queue/${id}/reject`, { method: 'POST', body: input });
}

// ---------- Voice ----------

export interface TranscribeResponse {
  status: 'transcribed' | 'queued' | 'cached' | 'error';
  transcript?: string;
  audioSeconds?: number;
  attachmentId?: string;
  reason?: string;
  message?: string;
}

export async function transcribeVoice(
  input: VoiceTranscribeRequest,
): Promise<TranscribeResponse> {
  return api('/ai/voice/transcribe', { method: 'POST', body: input });
}

export interface StructureResponse {
  status: 'structured' | 'error';
  draft?: VoiceStructureToolResult;
  reason?: string;
  message?: string;
}

export async function structureTranscript(
  input: VoiceStructureRequest,
): Promise<StructureResponse> {
  return api('/ai/voice/structure', { method: 'POST', body: input });
}

export async function fetchVendorEmailInbox(): Promise<{
  inbox: { address: string; isActive: boolean };
}> {
  return api('/ai/vendor-email-inbox');
}

// ---------- WO suggestion ----------

export async function acceptAiSuggestion(
  workOrderId: string,
  input: AiClassificationAcceptInput,
): Promise<{ status: string; category?: string; severity?: string }> {
  return api(`/work-orders/${workOrderId}/ai-suggestion/accept`, {
    method: 'POST',
    body: input,
  });
}

export async function dismissAiSuggestion(
  workOrderId: string,
): Promise<{ status: string }> {
  return api(`/work-orders/${workOrderId}/ai-suggestion/dismiss`, {
    method: 'POST',
    body: {},
  });
}

// ---------- Admin ----------

export interface AdminAiUsage {
  sinceUtc: string;
  breakdown: Array<{ feature: string; status: string; calls: number; spendUsd: number }>;
}

export async function fetchAdminAiUsage(): Promise<AdminAiUsage> {
  return api('/admin/ai/usage');
}

export interface AdminAiOrgUsage {
  org: { id: string; slug: string };
  usage: {
    spendTodayUsd: number;
    byFeatureThisMonth: Array<{ feature: string; calls: number; spendUsd: number }>;
  };
  quota: {
    dailyUsdCap: number;
    monthlyUsdCap: number;
    isPaused: boolean;
    featuresEnabled: Record<string, boolean>;
  };
}

export async function fetchAdminAiOrgUsage(orgId: string): Promise<AdminAiOrgUsage> {
  return api(`/admin/ai/usage/${orgId}`);
}

export async function updateAiQuota(
  orgId: string,
  input: AiQuotaUpdateInput,
): Promise<{ status: string }> {
  return api(`/admin/ai/orgs/${orgId}/quota`, { method: 'PATCH', body: input });
}

export async function pauseAi(
  orgId: string,
  input: AiQuotaPauseInput,
): Promise<{ status: string }> {
  return api(`/admin/ai/orgs/${orgId}/pause`, { method: 'POST', body: input });
}

export async function resumeAi(orgId: string): Promise<{ status: string }> {
  return api(`/admin/ai/orgs/${orgId}/resume`, { method: 'POST', body: {} });
}
